import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { pick } from "lodash";
import { Subject } from "rxjs";
import { Controller, FormProvider, useFormContext, UseFormReturn } from "react-hook-form";
import { trackPromise, usePromiseTracker } from "react-promise-tracker";

import { DropdownMultiSelect, LoadingSpinner } from "common";
import { FatOutlineButton } from "common/component/FatOutlineButton";
import { ProviderPortalFeatureFlag, useFeatureFlag } from "featureflag";
import { SPLIT_DELIMITER, splitAndRemoveEmpty } from "placement/const";

import { ModalDatePicker } from "../../form-components/ModalDatePicker";
import { ModalMultiSelectionInput } from "../../form-components/ModalMultiSelectionInput";
import { ModalSelectionInput } from "../../form-components/ModalSelectionInput";
import { ModalTextAreaInput } from "../../form-components/ModalTextAreaInput";
import { ClinicalRecommendationInput } from "../../form-components/auth-hours/ClinicalRecommendationInput";
import { ClientOfferInput } from "../../form-components/auth-hours/ClientOfferInput";

import { usePreSubmitValidation } from "../ClientOfferValidation";
import {
    getFieldsToResetForHourVisibility,
    getHourVisibility,
    ServiceLocationFieldMapping,
} from "../../form-components/auth-hours/resetHiddenFields";
import {
    ClientReportReportType,
    ClientReportReportTypeFromValueMap,
    TreatmentHoldInitiatorMap,
    ReportServiceType,
    YesNoOptionMap
} from "../../domain";
import {
    CanContinueOptionMap,
    ClinicalRecommendationChangeOptionMap,
    ClientServicesReceivedOption,
    ClientServicesReceivedOptionMap,
    ServiceLocationOptionMap
} from "./domain";
import { ClientReturnsTransferClinicalChangesInput } from "./ClientReturnsTransferClinicalChangesInput";
import { useClientTransferValidation } from "./ClientReturnsTransferSubmitRequestValidation";
import { ClientReturnsTransferRequestInput } from "./ClientTransferReturnRequestFormAuthModal";

type ClientReturnsTransferFormProps = {
    visible$: Subject<boolean>;
    methods: UseFormReturn<ClientReturnsTransferRequestInput>;
    onFinished: (formValues: ClientReturnsTransferRequestInput | null, isDraft: boolean) => Promise<void>;
    onCancel: () => Promise<void>;
    externalLoading: boolean;
    restrictedServiceTypeOpts: Map<string, string> | null;
    recordAuthType: 'treatment' | 'assessment' | 'transfer-re',
    authorizationGuid?: string,
    defaultClinicalRecommendationFromAuth?: Record<string, any>,
    clinicalRecommendationHoursFromAuth?: Record<string, any>
}

// ============================================================================
// FIELD LISTS FOR FORM OPERATIONS
// ============================================================================

const SUBMITTED_FIELDS: Array<keyof ClientReturnsTransferRequestInput> = [
    'clientServicesReceived',
    'requestInitiator',
    'familyNotifiedOfTransferNeed',
    'describeAttemptsMadeToResolveIssue',
    'dateOfLastService',
    'dateOfLastFullScheduleOffer',
    'serviceProvidedDuringTransfer',
    'canContinueProvidingServices',
    'clientReturnDate',
    'offeredParaprofessionalHours',
    'offeredMidLevelHours',
    'offeredHighLevelHours',
    'offeredGroupCareHours',
    'serviceType',
    'caregiverMidLevelHoursMin',
    'caregiverMidLevelHoursMax',
    'caregiverHighLevelHoursMin',
    'caregiverHighLevelHoursMax',
    'practitionerParaprofessionalHoursMin',
    'practitionerParaprofessionalHoursMax',
    'practitionerMidLevelHoursMin',
    'practitionerMidLevelHoursMax',
    'practitionerHighLevelHoursMin',
    'practitionerHighLevelHoursMax',
    'practitionerGroupCareHoursMin',
    'practitionerGroupCareHoursMax',
    'clinicalRecommendationChanging',
    'clinicalRecommendationChange',
    'reasonForRequest',
    'rationale',
    'paraprofessionalLocation',
    'midLevelLocation',
    'highLevelLocation',
    'assessmentAppointmentOfferedDate',
    'wasFsoOffered',
    'groupCareLocation',
    'assessmentLocation',
];

const FAMILY_NOTIFIED_RESET_FIELDS: Array<keyof ClientReturnsTransferRequestInput> = [
    'requestInitiator',
    'clientServicesReceived',
    'reasonForRequest',
    'describeAttemptsMadeToResolveIssue',
    'clientReturnDate',
    'wasFsoOffered',
    'dateOfLastFullScheduleOffer',
    'dateOfLastService',
    'serviceProvidedDuringTransfer',
    'canContinueProvidingServices',
    'offeredParaprofessionalHours',
    'offeredMidLevelHours',
    'offeredHighLevelHours',
    'offeredGroupCareHours',
    'assessmentAppointmentOfferedDate',
    'clinicalRecommendationChanging',
    'clinicalRecommendationChange',
    'rationale',
    'paraprofessionalLocation',
    'midLevelLocation',
    'highLevelLocation',
    'groupCareLocation',
    'assessmentLocation',
];

const SERVICE_LOCATION_FIELDS: Array<keyof ClientReturnsTransferRequestInput> = [
    'paraprofessionalLocation',
    'midLevelLocation',
    'highLevelLocation',
    'groupCareLocation',
    'assessmentLocation',
];

const SERVICE_LOCATION_FIELD_MAPPING: ServiceLocationFieldMapping = {
    paraprofessional: 'paraprofessionalLocation',
    practitionerMid: 'midLevelLocation',
    practitionerHigh: 'highLevelLocation',
    groupCare: 'groupCareLocation',
};

const RESET_ALL_HIDDEN_FIELDS_OPTIONS = {
    offeredHours: true,
    hourRanges: true,
    serviceLocations: true,
    locationMapping: SERVICE_LOCATION_FIELD_MAPPING,
};

// ============================================================================
// MAIN FORM COMPONENT
// ============================================================================

export function ClientReturnsTransferForm({
    visible$,
    methods,
    externalLoading,
    onFinished,
    onCancel,
    restrictedServiceTypeOpts,
    recordAuthType,
    authorizationGuid,
    defaultClinicalRecommendationFromAuth,
    clinicalRecommendationHoursFromAuth
}: ClientReturnsTransferFormProps) {
    const isUpdatedTransferRequestDisabled = useFeatureFlag(ProviderPortalFeatureFlag.TDTL_9950_UPDATED_TRANSFER_REQUEST_DISABLED);

    const isDraftRef = useRef(false);
    const formRef = useRef<HTMLFormElement>(null);

    const submitWithTracking = useCallback(
        (formValues: ClientReturnsTransferRequestInput | null, isDraft: boolean = false) => trackPromise(onFinished(formValues, isDraft)),
        [onFinished]
    );

    const { promiseInProgress } = usePromiseTracker();
    const { validateClientTransfer } = useClientTransferValidation();
    const { validateBeforeSubmit } = usePreSubmitValidation();
    const isCallingBackend = promiseInProgress || externalLoading;
    const familyNotifiedOfTransferNeed = methods.watch('familyNotifiedOfTransferNeed');
    const isDraftDisabled = isCallingBackend || !familyNotifiedOfTransferNeed;

    const submitAction = useCallback(async (data: ClientReturnsTransferRequestInput, isDraft: boolean) => {
        await submitWithTracking(pick(data, SUBMITTED_FIELDS), isDraft);
        if (!isDraft) {
            methods.reset();
        }
        isDraftRef.current = false;
        visible$.next(false);
    }, [submitWithTracking, methods, visible$]);

    const legacySubmit = useLegacySubmit({
        methods,
        visible$,
        submitWithTracking,
        validateBeforeSubmit,
        validateClientTransfer,
        authorizationGuid,
    });

    const submit = useCallback(
        async (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();

            if (isUpdatedTransferRequestDisabled) {
                return legacySubmit(e);
            }

            const isDraft = isDraftRef.current;

            if (isDraft) {
                // Draft: skip all validation, just get raw form values
                const data = methods.getValues();
                await submitAction(data as ClientReturnsTransferRequestInput, true);
            } else {
                // Non-draft: validate form fields first, then check server-side validation
                await methods.handleSubmit(async (data: ClientReturnsTransferRequestInput) => {
                    // Check if family was notified
                    if (data.familyNotifiedOfTransferNeed === 'NO') {
                        methods.setError('familyNotifiedOfTransferNeed', {
                            type: 'manual',
                            message: 'You cannot proceed, you must notify the family first'
                        });
                        return;
                    }
                    await validateBeforeSubmit(
                        () => validateClientTransfer(authorizationGuid),
                        async () => await submitAction(data, false)
                    );
                })(e);
            }
        },
        [methods, validateBeforeSubmit, validateClientTransfer, authorizationGuid, submitAction, isUpdatedTransferRequestDisabled, legacySubmit]
    );

    const saveAsDraft = useCallback(() => {
        isDraftRef.current = true;
        formRef.current?.requestSubmit();
    }, []);

    return (
        <FormProvider {...methods}>
            <form ref={formRef} autoComplete="off" onSubmit={submit}>
                {!isCallingBackend && (<>
                    <div className="space-y-6">
                        <div className="flex flex-col w-full p-4 scrollbar-always"
                            style={{ maxHeight: "calc(100vh - 68px - 100px - 37px - 90px - 60px)", minHeight: "30vh" }}>
                            {isUpdatedTransferRequestDisabled ? (
                                <LegacyFormFieldsSection
                                    isCallingBackend={isCallingBackend}
                                    recordAuthType={recordAuthType}
                                    restrictedServiceTypeOpts={restrictedServiceTypeOpts}
                                />
                            ) : (
                                <>
                                    <FamilyNotifiedInput disabled={isCallingBackend} />
                                    <FormFieldsSection
                                        isCallingBackend={isCallingBackend}
                                        recordAuthType={recordAuthType}
                                        restrictedServiceTypeOpts={restrictedServiceTypeOpts}
                                        defaultClinicalRecommendationFromAuth={defaultClinicalRecommendationFromAuth}
                                        clinicalRecommendationHoursFromAuth={clinicalRecommendationHoursFromAuth}
                                    />
                                </>
                            )}
                        </div>
                    </div>

                    <div className="w-full flex border-t dark:border-gray-600 p-4 justify-end">
                        <div className="flex">
                            {!isUpdatedTransferRequestDisabled && (
                                <>
                                    <FatOutlineButton color={'atlantis'}
                                        text={"Save As Draft"}
                                        minWidth="140px"
                                        disabled={isDraftDisabled}
                                        onClick={saveAsDraft} />
                                    <span style={{ width: 20 }} />
                                </>
                            )}
                            <FatOutlineButton color={'red'}
                                text={"Cancel"}
                                onClick={onCancel} />
                            <span style={{ width: 20 }} />
                            <FatOutlineButton type="submit" text={"Submit"} color={"green"} />
                        </div>
                    </div>
                </>)}

                {isCallingBackend &&
                    <div className="p-24">
                        <LoadingSpinner text="Processing" />
                    </div>
                }
            </form>
        </FormProvider>
    );
}

// ============================================================================
// SUB-COMPONENTS (Alphabetical Order)
// ============================================================================

const DateOfLastFullScheduleOfferInput = ({ disabled }: { disabled: boolean }) => {
    const { watch } = useFormContext<ClientReturnsTransferRequestInput>();
    const wasFsoOffered = watch("wasFsoOffered");
    const shouldNotRequired = wasFsoOffered === 'NO';

    return (
        <ModalDatePicker<ClientReturnsTransferRequestInput>
            label={'Date Of Last Full Schedule Offer'}
            disabled={disabled}
            field={"dateOfLastFullScheduleOffer"}
            required={!shouldNotRequired}
            tooltipContent="An offer of an ongoing schedule of appointments that fulfills the minimum hours authorized. All hours must be outside of the Client’s Mandated Hours, and the schedule must begin within 10 business days of the last direct appointment"
        />
    );
};

const ClinicalRecommendationSection = ({ disabled, restrictedServiceTypeOpts, defaultClinicalRecommendationFromAuth, clinicalRecommendationHoursFromAuth, recordAuthType }: {
    disabled: boolean,
    restrictedServiceTypeOpts: Map<string, string> | null,
    defaultClinicalRecommendationFromAuth?: Record<string, any>,
    clinicalRecommendationHoursFromAuth?: Record<string, any>,
    recordAuthType: 'treatment' | 'assessment' | 'transfer-re'
}) => {
    const { watch, setValue } = useFormContext<ClientReturnsTransferRequestInput>();
    const serviceReceived = watch("clientServicesReceived");
    const clinicalRecommendationChanging = watch("clinicalRecommendationChanging");
    const serviceType = watch("serviceType");
    const hideOfferHours = watch("canContinueProvidingServices") === 'NO';
    const isTreatmentAuth = recordAuthType === 'treatment';

    const included = [ClientServicesReceivedOption.YES_TREATMENT, ClientServicesReceivedOption.YES_ASSESSMENT, ClientServicesReceivedOption.TRANSFER_REEVALUATION].includes(
        ClientServicesReceivedOptionMap.get(serviceReceived ?? "") as ClientServicesReceivedOption
    );

    const isChanging = clinicalRecommendationChanging === 'YES';

    // Reset clinical recommendation fields when not included (service received is NO or TRANSFER_REEVALUATION)
    useEffect(() => {
        if (!included) {
            const fieldsToReset = [
                'offeredParaprofessionalHours',
                'offeredMidLevelHours',
                'offeredHighLevelHours',
                'offeredGroupCareHours',
                'clinicalRecommendationChanging',
                'clinicalRecommendationChange',
                'rationale',
                ...SERVICE_LOCATION_FIELDS,
            ];
            fieldsToReset.forEach(field => setValue(field as keyof ClientReturnsTransferRequestInput, null));
            setValue('serviceType', []);
            getFieldsToResetForHourVisibility(
                getHourVisibility([]),
                { hourRanges: true },
            ).forEach((field) => setValue(field as keyof ClientReturnsTransferRequestInput, null));
        }
    }, [included, setValue]);

    // When the provider cannot continue services during transfer, the Offer
    // hours block is hidden entirely - clear any entered values so nothing
    // stale is submitted.
    useEffect(() => {
        if (hideOfferHours) {
            ['offeredParaprofessionalHours', 'offeredMidLevelHours', 'offeredHighLevelHours', 'offeredGroupCareHours']
                .forEach(field => setValue(field as keyof ClientReturnsTransferRequestInput, null));
        }
    }, [hideOfferHours, setValue]);

    // Reset hidden hour-range/offered-hour/service-location fields when serviceType changes
    const previousServiceTypeRef = useRef<string[] | null>(null);
    useEffect(() => {
        const prev = previousServiceTypeRef.current;
        const current = serviceType ?? [];
        const hasActuallyChanged = prev !== null && (
            prev.length !== current.length ||
            current.some((v, i) => v !== prev[i])
        );
        if (hasActuallyChanged) {
            getFieldsToResetForHourVisibility(
                getHourVisibility(current as ReportServiceType[]),
                RESET_ALL_HIDDEN_FIELDS_OPTIONS,
            ).forEach((field) => setValue(field as keyof ClientReturnsTransferRequestInput, null));
        }
        previousServiceTypeRef.current = current;
    }, [serviceType, setValue]);

    // When clinical recommendation is not changing (isChanging === false means hours are disabled), set to default values.
    // Auth-based defaults only apply to treatment auths - for assessment/transfer-re auths the
    // fields start empty and the user must enter them (Service Types and hours are required).
    useEffect(() => {
        if (!isChanging && isTreatmentAuth && defaultClinicalRecommendationFromAuth) {
            Object.entries(defaultClinicalRecommendationFromAuth).forEach(([key, value]) => {
                setValue(key as keyof ClientReturnsTransferRequestInput, value);
            });
            if (clinicalRecommendationHoursFromAuth) {
                // Only populate hour fields that apply to the selected service types;
                // mirror useClientReportModalPrefill behavior.
                const hours = {...clinicalRecommendationHoursFromAuth};
                getFieldsToResetForHourVisibility(
                    getHourVisibility(serviceType as ReportServiceType[]),
                    {hourRanges: true},
                ).forEach((field) => hours[field] = null);
                Object.entries(hours).forEach(([key, value]) => {
                    setValue(key as keyof ClientReturnsTransferRequestInput, value as never);
                });
            }
        } else if (isChanging) {
            // Clear hour range/offered fields that don't apply to the current serviceType,
            // so auth-default values (which may span different service types) don't pollute editable fields
            getFieldsToResetForHourVisibility(
                getHourVisibility(serviceType as ReportServiceType[]),
                { offeredHours: true, hourRanges: true, serviceLocations: false },
            ).forEach((field) => setValue(field as keyof ClientReturnsTransferRequestInput, null));
        }
    }, [isChanging, isTreatmentAuth, setValue, defaultClinicalRecommendationFromAuth, clinicalRecommendationHoursFromAuth, serviceType]);

    const serviceLocationsFeat = useMemo(() => ({
        serviceLocationParaprofessionalInput: <ModalMultiSelectionInput<ClientReturnsTransferRequestInput>
                        options={Array.from(ServiceLocationOptionMap.keys())}
                        labelMapperFunc={(v) => ServiceLocationOptionMap.get(v) ?? v}
                        label={'Paraprofessional Location'}
                        disabled={disabled}
                        field={'paraprofessionalLocation'}
                        required
                    />,
        serviceLocationHighLevelInput: <ModalMultiSelectionInput<ClientReturnsTransferRequestInput>
                        options={Array.from(ServiceLocationOptionMap.keys())}
                        labelMapperFunc={(v) => ServiceLocationOptionMap.get(v) ?? v}
                        label={'High Level Location'}
                        disabled={disabled}
                        field={'highLevelLocation'}
                        required
                    />,
        serviceLocationMidLevelInput: <ModalMultiSelectionInput<ClientReturnsTransferRequestInput>
                        options={Array.from(ServiceLocationOptionMap.keys())}
                        labelMapperFunc={(v) => ServiceLocationOptionMap.get(v) ?? v}
                        label={'Mid Level Location'}
                        disabled={disabled}
                        field={'midLevelLocation'}
                        required
                    />,
        serviceLocationGroupCareInput: <ModalMultiSelectionInput<ClientReturnsTransferRequestInput>
                        options={Array.from(ServiceLocationOptionMap.keys())}
                        labelMapperFunc={(v) => ServiceLocationOptionMap.get(v) ?? v}
                        label={'Group Care Location'}
                        disabled={disabled}
                        field={'groupCareLocation'}
                        required
                    />,
    }), [disabled]);

    if (!included) {
        return null;
    }

    return (
        <>
            {!hideOfferHours &&
                <ClientOfferInput isNew={false}
                    isReturnsTransferRequest={true}
                    disabled={disabled}
                    tooltip="An appointment offered by the Provider to the Client and is documented in the Client’s record (pulled from provider manual)"
                    reportType={ClientReportReportTypeFromValueMap.get(ClientReportReportType.ABA_PROGRESS_REPORT)!} />
            }
            <ClinicalRecommendationInput disabled={disabled}
                disabledForHours={isTreatmentAuth && !isChanging}
                restrictedOptions={restrictedServiceTypeOpts}
                clinicalRecommendationChangingComponent={
                    <div>
                        <div className="pb-2">
                            <ModalSelectionInput<ClientReturnsTransferRequestInput>
                                options={YesNoOptionMap}
                                label={"Clinical Recommendation Changing"}
                                disabled={disabled}
                                field={'clinicalRecommendationChanging'}
                            />
                        </div>
                        {
                            isChanging &&
                            <div className="pb-2">
                                <ClientReturnsTransferClinicalChangesInput sending={disabled} />
                            </div>
                        }
                    </div>
                }
                serviceLocationsFeat={serviceLocationsFeat}
                reportType={ClientReportReportTypeFromValueMap.get(ClientReportReportType.ABA_PROGRESS_REPORT)!} />
        </>
    );
}

// TDTL-8567
const InputsForNoServiceSelected = ({ disabled, recordAuthType }: { disabled: boolean, recordAuthType: 'treatment' | 'assessment' | 'transfer-re' }) => {
    const { watch, setValue } = useFormContext<ClientReturnsTransferRequestInput>();
    const serviceReceived = watch("clientServicesReceived");
    const isNoServiceReceived = [ClientServicesReceivedOption.NO].includes(
        ClientServicesReceivedOptionMap.get(serviceReceived ?? "") as ClientServicesReceivedOption
    );

    useEffect(() => {
        if (!isNoServiceReceived) {
            const fieldsToReset = [
                'assessmentAppointmentOfferedDate',
            ];

            fieldsToReset.forEach(field => setValue(field as keyof ClientReturnsTransferRequestInput, null));
        }
    }, [isNoServiceReceived, setValue]);

    if (!isNoServiceReceived) {
        return null;
    }

    return (
        <>
            {
                (recordAuthType === 'transfer-re' || recordAuthType === 'assessment') && (
                    <ModalDatePicker<ClientReturnsTransferRequestInput>
                        label={'Assessment Appointment Offered Date'}
                            disabled={disabled}
                            field={"assessmentAppointmentOfferedDate"}
                            onChangeCallback={(value) => {
                                if (value) {
                                    setValue("wasFsoOffered", 'YES');
                                }
                            }}
                        />
                    )
                }
            </>
        );
}
    

const TransferRequestInputSection = ({ disabled }: { disabled: boolean }) => {
    const { watch, setValue } = useFormContext<ClientReturnsTransferRequestInput>();
    const serviceReceived = watch("clientServicesReceived");
    const isTreatmentOrAssessment = [ClientServicesReceivedOption.YES_TREATMENT, ClientServicesReceivedOption.YES_ASSESSMENT, ClientServicesReceivedOption.TRANSFER_REEVALUATION].includes(
        ClientServicesReceivedOptionMap.get(serviceReceived ?? "") as ClientServicesReceivedOption
    );

    useEffect(() => {
        if (!isTreatmentOrAssessment) {
            const fieldsToReset = [
                'serviceProvidedDuringTransfer',
                'canContinueProvidingServices',
            ];

            fieldsToReset.forEach(field => setValue(field as keyof ClientReturnsTransferRequestInput, null));
        }
    }, [isTreatmentOrAssessment, setValue]);

    return (
        <>
            <div className="w-full text-black-200 pt-2 pb-2 text-base flex items-center gap-2">
                <div className="font-bold">Offer & Service Continuity</div>
            </div>
            {isTreatmentOrAssessment && (
                <>
                    <ModalSelectionInput<ClientReturnsTransferRequestInput>
                        options={YesNoOptionMap}
                        label={'Service Provided During Transfer'}
                        disabled={disabled}
                        field={'serviceProvidedDuringTransfer'}
                    />
                    <ModalSelectionInput<ClientReturnsTransferRequestInput>
                        options={CanContinueOptionMap}
                        label={'Can Continue Providing Services During Transfer?'}
                        disabled={disabled}
                        field={'canContinueProvidingServices'}
                    />
                </>
            )}
            <ModalSelectionInput<ClientReturnsTransferRequestInput>
                options={YesNoOptionMap}
                label={'Was FSO Offered?'}
                disabled={disabled}
                field={"wasFsoOffered"}
            />
            <DateOfLastFullScheduleOfferInput disabled={disabled}/>
            <ModalDatePicker<ClientReturnsTransferRequestInput>
                label={'Most Recent Appointment Offered'}
                disabled={disabled}
                field={'dateOfLastService'}
                tooltipContent="The most recent date on which the Provider extended an appointment offer to the Client, as documented in the Client’s record."
            />
        </>
    );
}

const FormFieldsSection = ({
    isCallingBackend,
    recordAuthType,
    restrictedServiceTypeOpts,
    defaultClinicalRecommendationFromAuth,
    clinicalRecommendationHoursFromAuth
}: {
    isCallingBackend: boolean,
    recordAuthType: 'treatment' | 'assessment' | 'transfer-re',
    restrictedServiceTypeOpts: Map<string, string> | null,
    defaultClinicalRecommendationFromAuth?: Record<string, any>,
    clinicalRecommendationHoursFromAuth?: Record<string, any>,
}) => {
    const { watch } = useFormContext<ClientReturnsTransferRequestInput>();
    const familyNotifiedOfTransferNeed = watch("familyNotifiedOfTransferNeed");

    // Only show form fields if Family Notified is "YES"
    if (familyNotifiedOfTransferNeed !== 'YES') {
        return null;
    }

    return (
        <>
            <div className='my-1'></div>
            <ModalSelectionInput<ClientReturnsTransferRequestInput>
                options={TreatmentHoldInitiatorMap}
                label={'Request Initiator'}
                disabled={isCallingBackend}
                field={"requestInitiator"}/>
            <ModalSelectionInput<ClientReturnsTransferRequestInput>
                options={ClientServicesReceivedOptionMap}
                label={"Did Client Receive Service?"}
                disabled={isCallingBackend}
                field={'clientServicesReceived'} />
            <ModalTextAreaInput
                label={'Reason for Request'}
                disabled={isCallingBackend}
                field={"reasonForRequest"}
                maxLength={5000}
            />
            <ModalTextAreaInput
                label={'Describe attempts made to resolve issue'}
                disabled={isCallingBackend}
                field={'describeAttemptsMadeToResolveIssue'}
                maxLength={4000}
                required={true}
            />
            <ModalDatePicker<ClientReturnsTransferRequestInput>
                label={'Client Return Date'}
                disabled={isCallingBackend}
                field={"clientReturnDate"}
                tooltipContent="The date on which the provider indicates they will no longer be able to offer treatment, including full‑schedule and partial‑schedule options."
            />
            <InputsForNoServiceSelected disabled={isCallingBackend} recordAuthType={recordAuthType}/>
            <TransferRequestInputSection disabled={isCallingBackend}/>
            <ClinicalRecommendationSection disabled={isCallingBackend}
                restrictedServiceTypeOpts={restrictedServiceTypeOpts}
                defaultClinicalRecommendationFromAuth={defaultClinicalRecommendationFromAuth}
                clinicalRecommendationHoursFromAuth={clinicalRecommendationHoursFromAuth}
                recordAuthType={recordAuthType} />
        </>
    );
};

const FamilyNotifiedInput = ({ disabled }: { disabled: boolean }) => {
    const { watch, setValue } = useFormContext<ClientReturnsTransferRequestInput>();
    const familyNotifiedOfTransferNeed = watch("familyNotifiedOfTransferNeed");

    useEffect(() => {
        if (familyNotifiedOfTransferNeed === 'NO') {
            FAMILY_NOTIFIED_RESET_FIELDS.forEach(field => setValue(field, null));
        }
    }, [familyNotifiedOfTransferNeed, setValue]);

    return (
        <ModalSelectionInput<ClientReturnsTransferRequestInput>
            options={YesNoOptionMap}
            label={<span className="font-semibold">Family Notified Of Transfer Need?</span>}
            disabled={disabled}
            field={"familyNotifiedOfTransferNeed"}
            tooltipContent="Before initiating a transfer, please connect with the client and/or family to review the request together and ensure they understand the rationale. If additional support from CCS would be helpful, please submit a consultation request through the portal."
            required={true}
            errorStyle="warning"
        />
    );
};

// ============================================================================
// >>> LEGACY (TDTL-9950): pre-redesign behavior, shown only when
// ProviderPortalFeatureFlag.TDTL_9950_UPDATED_TRANSFER_REQUEST_DISABLED is on.
// When that flag is retired, delete this entire block, delete the
// `if (isUpdatedTransferRequestDisabled) { return legacySubmit(e); }` line in
// `submit`, collapse the `isUpdatedTransferRequestDisabled ? ... : ...` JSX
// ternary to its else-branch, remove the
// `{!isUpdatedTransferRequestDisabled && ...}` wrapper around the "Save As
// Draft" button, and drop the `useFeatureFlag`/`ProviderPortalFeatureFlag`
// import and the `isUpdatedTransferRequestDisabled` variable.
// ============================================================================

// master never had the TRANSFER_REEVALUATION option for "Did Client Receive
// Service?" - filter it out of the shared map for the legacy dropdown.
const LEGACY_CLIENT_SERVICES_RECEIVED_OPTION_MAP = new Map(
    Array.from(ClientServicesReceivedOptionMap.entries()).filter(
        ([, value]) => value !== ClientServicesReceivedOption.TRANSFER_REEVALUATION
    )
);

const LEGACY_SUBMITTED_FIELDS: Array<keyof ClientReturnsTransferRequestInput> = [
    'clientServicesReceived',
    'requestInitiator',
    'familyNotifiedOfTransferNeed',
    'describeAttemptsMadeToResolveIssue',
    'dateOfLastService',
    'dateOfLastFullScheduleOffer',
    'serviceProvidedDuringTransfer',
    'clientReturnDate',
    'offeredParaprofessionalHours',
    'offeredMidLevelHours',
    'offeredHighLevelHours',
    'offeredGroupCareHours',
    'serviceType',
    'caregiverMidLevelHoursMin',
    'caregiverMidLevelHoursMax',
    'caregiverHighLevelHoursMin',
    'caregiverHighLevelHoursMax',
    'practitionerParaprofessionalHoursMin',
    'practitionerParaprofessionalHoursMax',
    'practitionerMidLevelHoursMin',
    'practitionerMidLevelHoursMax',
    'practitionerHighLevelHoursMin',
    'practitionerHighLevelHoursMax',
    'practitionerGroupCareHoursMin',
    'practitionerGroupCareHoursMax',
    'clinicalRecommendationChanging',
    'clinicalRecommendationChange',
    'reasonForRequest',
    'assessmentAppointmentOfferedDate',
    'wasFsoOffered',
];

type UseLegacySubmitArgs = {
    methods: UseFormReturn<ClientReturnsTransferRequestInput>;
    visible$: Subject<boolean>;
    submitWithTracking: (formValues: ClientReturnsTransferRequestInput | null, isDraft?: boolean) => Promise<void>;
    validateBeforeSubmit: ReturnType<typeof usePreSubmitValidation>['validateBeforeSubmit'];
    validateClientTransfer: ReturnType<typeof useClientTransferValidation>['validateClientTransfer'];
    authorizationGuid?: string;
};

// Reproduces master's `submit` handler exactly: no draft support, no
// "family notified" guard, always a full non-draft submission.
const useLegacySubmit = ({
    methods,
    visible$,
    submitWithTracking,
    validateBeforeSubmit,
    validateClientTransfer,
    authorizationGuid,
}: UseLegacySubmitArgs) => useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
        await methods.handleSubmit(async (data: ClientReturnsTransferRequestInput) => {
            await validateBeforeSubmit(
                () => validateClientTransfer(authorizationGuid),
                async () => {
                    await submitWithTracking(pick(data, LEGACY_SUBMITTED_FIELDS));
                    methods.reset();
                    visible$.next(false);
                }
            );
        })(e);
    },
    [methods, visible$, submitWithTracking, validateBeforeSubmit, validateClientTransfer, authorizationGuid]
);

const LegacyDateOfLastFullScheduleOfferInput = ({ disabled }: { disabled: boolean }) => {
    const { watch } = useFormContext<ClientReturnsTransferRequestInput>();
    const wasFsoOffered = watch("wasFsoOffered");
    const shouldNotRequired = wasFsoOffered === 'NO';

    return (
        <ModalDatePicker<ClientReturnsTransferRequestInput>
            label={'Date Of Last Full Schedule Offer'}
            disabled={disabled}
            field={"dateOfLastFullScheduleOffer"}
            required={!shouldNotRequired}
        />
    );
};

// master's version of ClientReturnsTransferClinicalChangesInput - the current
// shared component (used only by this form) added a "Rationale" field that
// shouldn't show up in the legacy view.
const LegacyClientReturnsTransferClinicalChangesInput = ({ sending }: { sending: boolean }) => {
    const {
        control,
        setValue,
        watch,
        clearErrors,
        formState: { errors },
    } = useFormContext<ClientReturnsTransferRequestInput>();

    return (
        <div className="w-full text-black-200 pb-2 text-base items-center">
            <div className="flex items-center">
                <div className="w-2/6 text-right">
                    Clinical Recommendation Changes
                </div>
                <div className="w-3/6 ml-4 items-center">
                    <Controller
                        name="clinicalRecommendationChange"
                        control={control}
                        rules={{
                            required: true
                        }}
                        render={() => (
                            <DropdownMultiSelect
                                width={'w-full'}
                                field='clinicalRecommendationChange'
                                placeholder={'- Recommendation Changes'}
                                options={Array.from(ClinicalRecommendationChangeOptionMap.keys())}
                                labelMapperFunc={(val) => ClinicalRecommendationChangeOptionMap.get(val)!}
                                disabled={sending}
                                value={watch('clinicalRecommendationChange') ?? ""}
                                setValue={(value: string) => {
                                    setValue("clinicalRecommendationChange", splitAndRemoveEmpty(value).join(SPLIT_DELIMITER));
                                    clearErrors("clinicalRecommendationChange");
                                }}
                                hasError={errors.clinicalRecommendationChange !== undefined}
                            />
                        )}/>
                </div>
            </div>

            {
                errors.clinicalRecommendationChange &&
                <div className='flex'>
                    <div className={'w-2/6'}/>
                    <div className="w-3/6 ml-4 items-center text-red-700">
                        Please select at least 1 option for the Clinical Recommendation Changes field
                    </div>
                </div>
            }
        </div>
    );
};

const LegacyClinicalRecommendationSection = ({ disabled, restrictedServiceTypeOpts }: {
    disabled: boolean,
    restrictedServiceTypeOpts: Map<string, string> | null
}) => {
    const { watch, setValue } = useFormContext<ClientReturnsTransferRequestInput>();
    const serviceReceived = watch("clientServicesReceived");
    const clinicalRecommendationChanging = watch("clinicalRecommendationChanging");
    const included = [ClientServicesReceivedOption.YES_TREATMENT, ClientServicesReceivedOption.YES_ASSESSMENT].includes(
        ClientServicesReceivedOptionMap.get(serviceReceived ?? "") as ClientServicesReceivedOption
    );

    useEffect(() => {
        if (!included) {
            const fieldsToReset = [
                'offeredParaprofessionalHours',
                'offeredMidLevelHours',
                'offeredHighLevelHours',
                'offeredGroupCareHours',
                'clinicalRecommendationChanging',
                'clinicalRecommendationChange'
            ];

            fieldsToReset.forEach(field => setValue(field as keyof ClientReturnsTransferRequestInput, null));
        }
    }, [included, setValue]);

    if (!included) {
        return null;
    }

    return (
        <>
            <ClinicalRecommendationInput disabled={disabled}
                restrictedOptions={restrictedServiceTypeOpts}
                clinicalRecommendationChangingComponent={
                    <div>
                        <div className="pb-2">
                            <ModalSelectionInput<ClientReturnsTransferRequestInput>
                                options={YesNoOptionMap}
                                label={"Clinical Recommendation Changing"}
                                disabled={disabled}
                                field={'clinicalRecommendationChanging'}
                            />
                        </div>
                        {
                            clinicalRecommendationChanging === 'YES' &&
                            <div className="pb-2">
                                <LegacyClientReturnsTransferClinicalChangesInput sending={disabled} />
                            </div>
                        }
                    </div>
                }
                reportType={ClientReportReportTypeFromValueMap.get(ClientReportReportType.ABA_PROGRESS_REPORT)!} />
            <ClientOfferInput isNew={false}
                isReturnsTransferRequest={true}
                disabled={disabled}
                reportType={ClientReportReportTypeFromValueMap.get(ClientReportReportType.ABA_PROGRESS_REPORT)!} />
        </>
    );
};

const LegacyTransferRequestInputSection = ({ disabled }: { disabled: boolean }) => {
    const { watch, setValue } = useFormContext<ClientReturnsTransferRequestInput>();
    const serviceReceived = watch("clientServicesReceived");
    const isTreatmentOrAssessment = [ClientServicesReceivedOption.YES_TREATMENT, ClientServicesReceivedOption.YES_ASSESSMENT].includes(
        ClientServicesReceivedOptionMap.get(serviceReceived ?? "") as ClientServicesReceivedOption
    );

    useEffect(() => {
        if (!isTreatmentOrAssessment) {
            const fieldsToReset = [
                'familyNotifiedOfTransferNeed',
                'describeAttemptsMadeToResolveIssue',
                'dateOfLastService',
                'serviceProvidedDuringTransfer',
            ];

            fieldsToReset.forEach(field => setValue(field as keyof ClientReturnsTransferRequestInput, null));
        }
    }, [isTreatmentOrAssessment, setValue]);

    if (!isTreatmentOrAssessment) {
        return null;
    }

    return (
        <>
            <ModalSelectionInput<ClientReturnsTransferRequestInput>
                options={YesNoOptionMap}
                label={"Family Notified Of Transfer Need"}
                disabled={disabled}
                field={'familyNotifiedOfTransferNeed'}
            />
            <ModalTextAreaInput
                label={'Describe attempts made to resolve issue'}
                disabled={disabled}
                field={'describeAttemptsMadeToResolveIssue'}
                showCharacterCount
                maxLength={2048}
            />
            <ModalSelectionInput<ClientReturnsTransferRequestInput>
                options={YesNoOptionMap}
                label={'Service Provided During Transfer'}
                disabled={disabled}
                field={'serviceProvidedDuringTransfer'}
            />
            <ModalDatePicker<ClientReturnsTransferRequestInput>
                label={'Date Of Last Service'}
                disabled={disabled}
                field={'dateOfLastService'}
            />
        </>
    );
};

const LegacyFormFieldsSection = ({
    isCallingBackend,
    recordAuthType,
    restrictedServiceTypeOpts,
}: {
    isCallingBackend: boolean,
    recordAuthType: 'treatment' | 'assessment' | 'transfer-re',
    restrictedServiceTypeOpts: Map<string, string> | null,
}) => (
    <>
        <div className='my-1'></div>
        <ModalSelectionInput<ClientReturnsTransferRequestInput>
            options={LEGACY_CLIENT_SERVICES_RECEIVED_OPTION_MAP}
            label={"Did Client Receive Service?"}
            disabled={isCallingBackend}
            field={'clientServicesReceived'} />
        <ModalSelectionInput<ClientReturnsTransferRequestInput>
            options={TreatmentHoldInitiatorMap}
            label={'Request Initiator'}
            disabled={isCallingBackend}
            field={"requestInitiator"}/>
        <ModalTextAreaInput
            label={'Reason for Request'}
            disabled={isCallingBackend}
            field={"reasonForRequest"}
            showCharacterCount
            maxLength={5000}
        />
        <ModalDatePicker<ClientReturnsTransferRequestInput>
            label={'Client Return Date'}
            disabled={isCallingBackend}
            field={"clientReturnDate"}
        />
        <InputsForNoServiceSelected disabled={isCallingBackend} recordAuthType={recordAuthType}/>
        <ModalSelectionInput<ClientReturnsTransferRequestInput>
            options={YesNoOptionMap}
            label={'Was FSO Offered'}
            disabled={isCallingBackend}
            field={"wasFsoOffered"}
        />
        <LegacyTransferRequestInputSection disabled={isCallingBackend}/>
        <LegacyDateOfLastFullScheduleOfferInput disabled={isCallingBackend}/>
        <LegacyClinicalRecommendationSection disabled={isCallingBackend}
            restrictedServiceTypeOpts={restrictedServiceTypeOpts} />
    </>
);

// <<< LEGACY (TDTL-9950)
// ============================================================================